import { BACKGROUNDS, CLOTH_COLORS, EYE_COLORS, HAIR_COLORS, LIP_COLORS, SKIN_TONES } from "@/lib/avatar/catalog";
import { pieceMarkup, pieceOf } from "@/lib/avatar/pieces";
import { Brows, Details, Earrings, Ears, Eyes, FACE_PATHS, FacialHair, Glasses, Mouth, Nose, Wrinkles } from "./parts/face";
import { HAIR_VOLUMES, HATS, HairBack, HairFront, Headwear } from "./parts/hair";
import { NECKS, Neckwear, Top, neckTransform } from "./parts/outfit";

// Coiffures dont la mèche de devant descend jusqu'au milieu de l'oreille.
const EAR_COVERING_HAIR = ["long", "longside", "wavy", "shoulder", "bob", "braids"];
// Celles dont les mèches tombent devant toute l'oreille : les boucles ne se voient plus, comme
// sous un voile. Dessinées quand même, elles dépassaient à moitié sous une mèche.
const EAR_HIDING_HAIR = ["dreads", "boxbraids"];

// Les couvre-chefs posés sur le crâne ou serrés autour de la tête : une coiffure ajoutée qui
// a un dessin plus ras (svgUnderHat) le prend sous eux, sinon elle dépasse au-dessus.
const CROWN_HATS = ["beret", "tophat", "gradcap", "crown", "partyhat", "headband", "bandana", "plate"];

const byId = (list, id) => list.find((item) => item.id === id) || list[0];

// Le dessin du personnage, à poser dans un <svg viewBox="0 0 120 120">. Aucun état,
// aucun style : il sert au composant animé du site comme au rendu en image fixe
// (e-mails, app). `classes` porte les classes d'animation quand il y en a ;
// `eyes`, `mouth`, `brows`, `blush` et `tear` laissent une réaction ou une humeur
// remplacer le visage composé.
// `pieces` : les pièces ajoutées sans redéploiement (lib/avatar/pieces.js), rangées par
// « catégorie:identifiant ». Une valeur hors catalogue qui n'y figure pas ne dessine rien.
export default function AvatarFigure({
    config,
    id,
    pieces = null,
    classes = {},
    eyes = config.eyes,
    mouth = config.mouth,
    brows = config.brows,
    mouthMotion = null,
    blush = false,
    tear = false,
    // Faux : le personnage seul, sans son rond ni son fond, pour le poser sur une bannière.
    frame = true,
}) {
    const skin = byId(SKIN_TONES, config.skin);
    const hair = byId(HAIR_COLORS, config.hairColor);
    const accent = byId(CLOTH_COLORS, config.headwearColor);
    const neck = NECKS[config.build] || NECKS.regular;
    const veiled = config.headwear === "hijab";
    // Sous un bonnet, une casquette ou un chapeau, les cheveux ne dépassent que sur les
    // côtés : la découpe suit le bord du couvre-chef puis s'arrondit, pour qu'une coiffure
    // volumineuse en sorte en bouffant au lieu d'être tranchée net.
    const topColor = byId(CLOTH_COLORS, config.topColor);
    const tones = { accent, hair, skin, top: topColor };
    // Une pièce ajoutée, ou null si la catégorie porte une pièce de base.
    const customHair = veiled ? null : pieceOf(pieces, "hair", config.hair);
    const customTop = pieceOf(pieces, "top", config.top);
    // Sous un couvre-chef posé sur le crâne ou serré autour de la tête, une coiffure haute prend son dessin plus ras.
    const flattened = Boolean(customHair?.svgUnderHat) && CROWN_HATS.includes(config.headwear);
    // Le volume de la coiffure : les couvre-chefs s'y ajustent (HATS).
    const volume = customHair ? (customHair.snug ? "snug" : customHair.wide && !flattened ? "wide" : "regular") : (Object.hasOwn(HAIR_VOLUMES, config.hair) && HAIR_VOLUMES[config.hair]) || "regular";
    // Pas de volume de cheveux : le couvre-chef se pose au ras du crâne.
    const snug = ["snug", "sides", "bare"].includes(volume);
    tones.snug = snug;
    // Le dessin d'une pièce ajoutée, dans les teintes de cet avatar.
    const custom = (key) => {
        const piece = pieceOf(pieces, key, config[key]);
        return piece ? { piece, html: pieceMarkup(piece, tones) } : null;
    };
    const customHeadwear = veiled ? null : custom("headwear");
    const customNeckwear = veiled ? null : custom("neckwear");
    const earsHidden = !veiled && !customHair && EAR_HIDING_HAIR.includes(config.hair);
    const customEarrings = veiled || earsHidden ? null : custom("earrings");
    const customGlasses = custom("glasses");
    const customDetails = custom("details");
    // Une bouche ajoutée. Quand une réaction ou une humeur demande une autre bouche, elle prend son
    // dessin ouvert pour le rire et la surprise, son dessin triste pour la tristesse ; à défaut, la bouche de base.
    const mouthPiece = pieceOf(pieces, "mouth", config.mouth);
    const mouthField = mouthPiece && (mouth === config.mouth ? "svg" : ["laugh", "grin", "ooh"].includes(mouth) && mouthPiece.svgOpen ? "svgOpen" : mouth === "sad" && mouthPiece.svgSad ? "svgSad" : null);
    const customMouth = mouthField && pieceMarkup(mouthPiece, tones, mouthField);
    const hairBack = customHair && pieceMarkup(customHair, tones, flattened ? "svgBackUnderHat" : "svgBack");
    const hairFront = customHair && pieceMarkup(customHair, tones, flattened ? "svgUnderHat" : "svg");
    const earsCovered = !veiled && (earsHidden || EAR_COVERING_HAIR.includes(config.hair) || Boolean(customHair?.coversEars));
    // Comment le couvre-chef se pose : celui d'une pièce ajoutée cache toujours le dessus des cheveux.
    const hat = customHeadwear ? customHeadwear.piece.hat && { ...customHeadwear.piece.hat, covers: true } : !veiled && Object.hasOwn(HATS, config.headwear) ? HATS[config.headwear] : null;
    const hairClip = hat?.covers ? `url(#${id}hat)` : undefined;
    const hatLine = hat?.line ?? 44;
    // Sa taille suit le volume de la coiffure, autour du point où il touche les tempes.
    const hatScale = (hat && typeof hat[volume] === "number" && hat[volume]) || 1;
    const hatFit = hatScale === 1 ? undefined : `translate(60 ${hatLine}) scale(${hatScale}) translate(-60 ${-hatLine})`;

    return (
        <>
            <clipPath id={`${id}frame`}>
                <circle cx="60" cy="60" r="60" />
            </clipPath>
            <clipPath id={`${id}face`}>
                <path d={FACE_PATHS[config.face]} />
            </clipPath>
            <clipPath id={`${id}jaw`}>
                <rect x="20" y="59" width="80" height="50" />
            </clipPath>
            <clipPath id={`${id}hat`}>
                <path transform={hatFit} d={`M-80 200V${hatLine + 13}H19.5q0.5-13 12.5-13${hat?.edge || "h56"}q12 0 12.5 13H200V200z`} />
            </clipPath>
            <g clipPath={frame ? `url(#${id}frame)` : undefined}>
                {frame && <rect width="120" height="120" fill={byId(BACKGROUNDS, config.background).fill} />}
                <g className={classes.body}>
                    <Top
                        style={config.top}
                        color={topColor}
                        skin={skin}
                        accent={accent}
                        build={config.build}
                        clipId={`${id}top`}
                        custom={customTop && { piece: customTop, html: pieceMarkup(customTop, tones), collar: pieceMarkup(customTop, tones, "svgCollar"), className: classes.piece }}
                    />
                </g>
                <g className={classes.lean}>
                    <g className={classes.head}>
                        {!veiled && (
                            <g clipPath={hairClip}>
                                {customHair ? hairBack && <g className={classes.piece} dangerouslySetInnerHTML={hairBack} /> : <HairBack style={config.hair} color={hair} underHat={hat?.covers ? hatLine : null} />}
                            </g>
                        )}
                        <path d={neck[0]} fill={skin.fill} />
                        <path d={neck[1]} fill={skin.shade} />
                        {!veiled && (
                            // Une pièce ajoutée peut refuser l'échelle du cou (neckScale: false) : une cape
                            // se pose sur les épaules et garde sa taille quelle que soit la carrure.
                            <g transform={customNeckwear?.piece.neckScale === false ? undefined : neckTransform(config.build)}>
                                {customNeckwear ? <g className={classes.piece} dangerouslySetInnerHTML={customNeckwear.html} /> : <Neckwear style={config.neckwear} color={accent} />}
                            </g>
                        )}
                        <Ears skin={skin} covered={earsCovered} />
                        {customEarrings ? <g className={classes.piece} dangerouslySetInnerHTML={customEarrings.html} /> : <Earrings style={veiled || earsHidden ? "none" : config.earrings} />}
                        <path d={FACE_PATHS[config.face]} fill={skin.fill} />
                        <Wrinkles style={config.wrinkles} skin={skin} />
                        {customDetails ? <g className={classes.piece} dangerouslySetInnerHTML={customDetails.html} /> : <Details style={config.details} skin={skin} />}
                        {blush && config.details !== "blush" && <Details style="blush" skin={skin} />}
                        <FacialHair style={config.facialHair} color={hair} clip={`url(#${id}face)`} jawClip={`url(#${id}jaw)`} facePath={FACE_PATHS[config.face]} patternId={`${id}stubble`} />
                        <Nose style={config.nose} skin={skin} />
                        {customMouth ? <g className={classes.piece} dangerouslySetInnerHTML={customMouth} /> : <Mouth key={`${mouth}-${mouthMotion?.kind}`} style={mouth} motion={mouthMotion} lip={byId(LIP_COLORS, config.lipColor)} />}
                        <Eyes
                            style={eyes}
                            iris={byId(EYE_COLORS, config.eyeColor)}
                            skin={skin}
                            className={classes.eyes}
                            gazeClass={classes.gaze}
                            pupilClass={classes.pupil}
                        />
                        <Brows style={brows} color={hair} className={classes.brows} />
                        {tear && (
                            <path
                                className={classes.tear}
                                d="M45.5 62.5c1.6 2.2 2.2 3.4 2.2 4.4a2.2 2.2 0 0 1-4.4 0c0-1 0.6-2.2 2.2-4.4z"
                                fill="#7CC4F5"
                            />
                        )}
                        {!veiled && (
                            // Le trait de même couleur arrondit les angles des mèches et des pattes.
                            <g stroke={hair.fill} strokeWidth="1.4" strokeLinejoin="round" clipPath={hairClip}>
                                {customHair ? hairFront && <g className={classes.piece} dangerouslySetInnerHTML={hairFront} /> : <HairFront style={config.hair} color={hair} />}
                            </g>
                        )}
                        <g transform={hatFit}>
                            {customHeadwear ? <g className={classes.piece} dangerouslySetInnerHTML={customHeadwear.html} /> : <Headwear style={config.headwear} color={accent} snug={snug} scale={hatScale} />}
                        </g>
                        {customGlasses ? <g className={classes.piece} dangerouslySetInnerHTML={customGlasses.html} /> : <Glasses style={config.glasses} />}
                    </g>
                </g>
            </g>
        </>
    );
}
